import { useState, useEffect } from 'react'
import {
  Box, Typography, TextField, MenuItem, Select, FormControl, InputLabel,
  Checkbox, FormControlLabel, Radio, RadioGroup, Button, Paper, CircularProgress,
  Alert, FormGroup, FormHelperText,
} from '@mui/material'
import { CheckCircle as SuccessIcon, Block as InactiveIcon } from '@mui/icons-material'
import { useParams, useSearchParams } from 'react-router'
import { publicFormsApi, PublicFormData, LeadFormField, FieldType } from '../lib/api/leadFormsApi'

// ── Individual field renderer ─────────────────────────────────────────────────

interface FieldRendererProps {
  field:    LeadFormField
  value:    any
  onChange: (val: any) => void
  error?:   string
}

function FieldRenderer({ field, value, onChange, error }: FieldRendererProps) {
  const label = `${field.label}${field.required ? ' *' : ''}`
  const opts  = field.configuration?.options ?? []

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '& fieldset': { borderColor: error ? '#d32f2f' : '#ccc' },
      '&:hover fieldset': { borderColor: error ? '#d32f2f' : '#9b87c8' },
      '&.Mui-focused fieldset': { borderColor: error ? '#d32f2f' : '#4B3082', borderWidth: '1.5px' },
    },
    '& .MuiInputBase-input': { fontSize: '15px', color: '#1a1a1a' },
    '& .MuiInputLabel-root': { fontSize: '15px' },
  }

  switch (field.fieldType as FieldType) {
    case 'textarea':
      return (
        <TextField
          label={label}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? ''}
          multiline
          rows={3}
          fullWidth
          size="small"
          error={!!error}
          helperText={error}
          sx={inputSx}
        />
      )
    case 'dropdown':
    case 'multi_select':
      return (
        <FormControl fullWidth size="small" error={!!error} sx={inputSx}>
          <InputLabel sx={{ fontSize: '15px' }}>{label}</InputLabel>
          <Select
            label={label}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            sx={{ fontSize: '15px' }}
          >
            {opts.map((o, i) => <MenuItem key={i} value={o.value} sx={{ fontSize: '15px' }}>{o.label}</MenuItem>)}
          </Select>
          {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      )
    case 'radio':
      return (
        <FormControl error={!!error}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333', mb: 0.5 }}>{label}</Typography>
          <RadioGroup value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
            {opts.map((o, i) => (
              <FormControlLabel
                key={i}
                value={o.value}
                control={<Radio size="small" sx={{ color: '#4B3082', '&.Mui-checked': { color: '#4B3082' } }} />}
                label={<Typography sx={{ fontSize: '14px' }}>{o.label}</Typography>}
              />
            ))}
          </RadioGroup>
          {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      )
    case 'checkbox': {
      const checked: string[] = Array.isArray(value) ? value : []
      return (
        <FormControl error={!!error}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333', mb: 0.5 }}>{label}</Typography>
          <FormGroup>
            {opts.map((o, i) => (
              <FormControlLabel
                key={i}
                control={
                  <Checkbox
                    size="small"
                    checked={checked.includes(o.value)}
                    onChange={(e) => {
                      if (e.target.checked) onChange([...checked, o.value])
                      else onChange(checked.filter(v => v !== o.value))
                    }}
                    sx={{ color: '#4B3082', '&.Mui-checked': { color: '#4B3082' } }}
                  />
                }
                label={<Typography sx={{ fontSize: '14px' }}>{o.label}</Typography>}
              />
            ))}
          </FormGroup>
          {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      )
    }
    case 'date':
      return (
        <TextField
          label={label}
          type="date"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          error={!!error}
          helperText={error}
          sx={inputSx}
        />
      )
    default: {
      const isPhone = field.fieldType === 'phone'
      const isName  = field.mappingField === 'name' || field.fieldName === 'name'
      return (
        <TextField
          label={label}
          value={value ?? ''}
          onChange={(e) => {
            let v = e.target.value
            // Phone: only digits, max 10
            if (isPhone) v = v.replace(/\D/g, '').slice(0, 10)
            // Name: max 50 chars
            if (isName)  v = v.slice(0, 50)
            onChange(v)
          }}
          placeholder={field.placeholder ?? ''}
          fullWidth
          size="small"
          type={
            field.fieldType === 'email'  ? 'email'
            : field.fieldType === 'number' ? 'number'
            : field.fieldType === 'url'    ? 'url'
            : 'text'
          }
          inputProps={{
            ...(isPhone ? { inputMode: 'numeric', maxLength: 10 } : {}),
            ...(isName  ? { maxLength: 50 }                       : {}),
          }}
          error={!!error}
          helperText={
            error
              ? error
              : isPhone && value
                ? `${(value ?? '').length}/10 digits`
                : isName && value
                  ? `${(value ?? '').length}/50`
                  : undefined
          }
          sx={inputSx}
        />
      )
    }
  }
}

// ── Validation ────────────────────────────────────────────────────────────────

function validate(fields: LeadFormField[], values: Record<string, any>): Record<string, string> {
  const errs: Record<string, string> = {}
  for (const f of fields) {
    if (!f.isVisible) continue
    const val = values[f.fieldName]
    if (f.required) {
      if (val === undefined || val === null || val === '') {
        errs[f.fieldName] = `${f.label} is required`
        continue
      }
      if (Array.isArray(val) && val.length === 0) {
        errs[f.fieldName] = `${f.label} is required`
        continue
      }
    }
    if (val && f.fieldType === 'email') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val))
        errs[f.fieldName] = 'Enter a valid email address (e.g. name@example.com)'
    }
    if (val && f.fieldType === 'phone') {
      const digits = String(val).replace(/\D/g, '')
      if (digits.length !== 10)
        errs[f.fieldName] = 'Phone number must be exactly 10 digits'
    }
    if (val && (f.mappingField === 'name' || f.fieldName === 'name')) {
      if (String(val).length > 50)
        errs[f.fieldName] = 'Name must be 50 characters or less'
    }
    if (val && f.fieldType === 'url' && !/^https?:\/\/.+\..+/.test(val)) {
      errs[f.fieldName] = 'Enter a valid URL (starting with http/https)'
    }
  }
  return errs
}

// ── Main Public Form Page ─────────────────────────────────────────────────────

type PageState = 'loading' | 'notfound' | 'inactive' | 'ready' | 'submitting' | 'success' | 'error'

export default function PublicFormPage() {
  const { slug }           = useParams<{ slug: string }>()
  const [searchParams]     = useSearchParams()

  const [form,     setForm]     = useState<PublicFormData | null>(null)
  const [pageState,setPageState]= useState<PageState>('loading')
  const [values,   setValues]   = useState<Record<string, any>>({})
  const [errors,   setErrors]   = useState<Record<string, string>>({})
  const [successMsg, setSuccessMsg] = useState('')

  // UTM params
  const utm = {
    source:   searchParams.get('utm_source')   ?? undefined,
    medium:   searchParams.get('utm_medium')   ?? undefined,
    campaign: searchParams.get('utm_campaign') ?? undefined,
    content:  searchParams.get('utm_content')  ?? undefined,
    term:     searchParams.get('utm_term')     ?? undefined,
  }

  useEffect(() => {
    if (!slug) { setPageState('notfound'); return }
    publicFormsApi.getForm(slug)
      .then(data => {
        setForm(data)
        if (data.status === 'Inactive') {
          setPageState('inactive')
          return
        }
        // Pre-fill defaults
        const defaults: Record<string, any> = {}
        data.fields.forEach(f => {
          if (f.configuration?.defaultValue) defaults[f.fieldName] = f.configuration.defaultValue
        })
        setValues(defaults)
        setPageState('ready')
      })
      .catch(() => setPageState('notfound'))
  }, [slug])

  const handleSubmit = async () => {
    if (!form) return
    const visibleFields = form.fields.filter(f => f.isVisible)
    const errs = validate(visibleFields, values)
    if (Object.keys(errs).length > 0) { setErrors(errs); return }

    setPageState('submitting')
    try {
      const res = await publicFormsApi.submit(slug!, {
        formData:    values,
        utmSource:   utm.source,
        utmMedium:   utm.medium,
        utmCampaign: utm.campaign,
        utmContent:  utm.content,
        utmTerm:     utm.term,
      })
      setSuccessMsg(form.successMessage || 'Thank you! We will get back to you soon.')
      setPageState('success')
      if (form.redirectUrl) {
        setTimeout(() => { window.location.href = form.redirectUrl! }, 2500)
      }
    } catch {
      setPageState('error')
    }
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (pageState === 'loading') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f4f2fa' }}>
        <CircularProgress sx={{ color: '#4B3082' }} />
      </Box>
    )
  }

  // ── Not Found ──────────────────────────────────────────────────────────────
  if (pageState === 'notfound' || (!form && pageState !== 'inactive')) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f4f2fa', p: 3 }}>
        <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center', borderRadius: '16px' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1 }}>Form Not Found</Typography>
          <Typography sx={{ color: '#666' }}>
            The link may be incorrect or the form no longer exists.
          </Typography>
        </Paper>
      </Box>
    )
  }

  // ── Inactive ───────────────────────────────────────────────────────────────
  if (pageState === 'inactive') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f4f2fa', p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 5, maxWidth: 480, textAlign: 'center', borderRadius: '16px',
            border: '1px solid #e0d4f7',
            boxShadow: '0 8px 32px rgba(75,48,130,0.10)',
          }}
        >
          <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                width: 72, height: 72, borderRadius: '50%',
                bgcolor: '#f3eeff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <InactiveIcon sx={{ fontSize: 36, color: '#4B3082' }} />
            </Box>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1 }}>
            This Form is Not Active
          </Typography>
          <Typography sx={{ color: '#666', lineHeight: 1.7, fontSize: '15px' }}>
            {form?.name && (
              <Box component="span" sx={{ display: 'block', fontWeight: 600, color: '#4B3082', mb: 0.5 }}>
                {form.name}
              </Box>
            )}
            This form is currently not accepting responses. Please contact the organization for more information.
          </Typography>
          <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid #ede8f8' }}>
            <Typography sx={{ fontSize: '12px', color: '#aaa' }}>
              Powered by{' '}
              <Box component="span" sx={{ color: '#4B3082', fontWeight: 700 }}>
                Momentum CRM
              </Box>
            </Typography>
          </Box>
        </Paper>
      </Box>
    )
  }

  // ── Success ────────────────────────────────────────────────────────────────
  if (pageState === 'success') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f4f2fa', p: 3 }}>
        <Paper sx={{ p: 5, maxWidth: 480, textAlign: 'center', borderRadius: '16px', border: '1px solid #e0d9f5' }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <SuccessIcon sx={{ fontSize: 56, color: '#4bca81' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1.5 }}>
            Submitted!
          </Typography>
          <Typography sx={{ color: '#555', lineHeight: 1.6 }}>
            {successMsg}
          </Typography>
          {form.redirectUrl && (
            <Typography sx={{ fontSize: '13px', color: '#999', mt: 2 }}>
              Redirecting you shortly…
            </Typography>
          )}
        </Paper>
      </Box>
    )
  }

  const visibleFields = form.fields.filter(f => f.isVisible).sort((a, b) => a.displayOrder - b.displayOrder)

  // ── Main form ──────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f4f2fa',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 4,
        px: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 560 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid #ddd9f0',
            boxShadow: '0 8px 32px rgba(75,48,130,0.15)',
          }}
        >
          {/* Header */}
          <Box sx={{ background: 'linear-gradient(135deg, #2F1E57 0%, #4B3082 100%)', px: 4, py: 3.5 }}>
            {(form.formLogoUrl || form.formCompanyName) ? (
              /* Branding layout: logo on left, [company name / form title] stacked on right */
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {form.formLogoUrl && (
                  <Box
                    component="img"
                    src={form.formLogoUrl}
                    alt="Company logo"
                    sx={{
                      width: 56, height: 56,
                      borderRadius: '12px',
                      objectFit: 'contain',
                      bgcolor: 'rgba(255,255,255,0.15)',
                      p: '6px',
                      flexShrink: 0,
                    }}
                  />
                )}
                <Box>
                  {form.formCompanyName && (
                    <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#fff', lineHeight: 1.2, letterSpacing: '-0.2px' }}>
                      {form.formCompanyName}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.72)', lineHeight: 1.4, mt: form.formCompanyName ? 0.3 : 0 }}>
                    {form.name}
                  </Typography>
                  {form.description && (
                    <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, mt: 0.25 }}>
                      {form.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              /* No branding — show form title prominently */
              <>
                <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#fff', mb: 0.5, letterSpacing: '-0.3px' }}>
                  {form.name}
                </Typography>
                {form.description && (
                  <Typography sx={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>
                    {form.description}
                  </Typography>
                )}
              </>
            )}
          </Box>

          {/* Fields */}
          <Box sx={{ px: 4, py: 3.5, bgcolor: '#fff', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {pageState === 'error' && (
              <Alert severity="error" sx={{ borderRadius: '8px' }}>
                Something went wrong. Please try again.
              </Alert>
            )}

            {visibleFields.map(field => (
              <FieldRenderer
                key={field.id}
                field={field}
                value={values[field.fieldName]}
                onChange={(val) => {
                  setValues(prev => ({ ...prev, [field.fieldName]: val }))
                  if (errors[field.fieldName]) setErrors(prev => { const e = { ...prev }; delete e[field.fieldName]; return e })
                }}
                error={errors[field.fieldName]}
              />
            ))}

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleSubmit}
              disabled={pageState === 'submitting'}
              sx={{
                mt: 1,
                py: 1.5,
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #2F1E57 0%, #4B3082 100%)',
                boxShadow: '0 4px 16px rgba(75,48,130,0.4)',
                '&:hover': { background: '#2F1E57' },
                '&:disabled': { opacity: 0.7 },
              }}
            >
              {pageState === 'submitting' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: '#fff' }} />
                  <span>Submitting…</span>
                </Box>
              ) : 'Submit'}
            </Button>
          </Box>

          {/* Footer */}
          <Box sx={{ px: 4, py: 1.5, bgcolor: '#faf9fe', borderTop: '1px solid #ede8f8', textAlign: 'center' }}>
            <Typography sx={{ fontSize: '12px', color: '#aaa' }}>
              Powered by{' '}
              <Box component="span" sx={{ color: '#4B3082', fontWeight: 700 }}>
                Momentum CRM
              </Box>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}
